import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { Box, CircularProgress, Typography } from '@mui/material';
import { loadTenantApp } from './tenantloader/TenantLoader';
import { ThemeProvider } from './theme/ThemeContext';
import ReviewApp from './review/ReviewApp';

// Banner is dev-only. Webpack replaces `process.env.NODE_ENV` at build time,
// so in production this ternary becomes `false ? require(...) : null` and the
// require() (and the AesharedModeBanner module itself) is dead-code eliminated
// — the banner code is not shipped in the production bundle.
// eslint-disable-next-line global-require
const AesharedModeBanner = process.env.NODE_ENV !== 'production'
  ? require('./AesharedModeBanner').default
  : null;
const ResearchApp = lazy(() => import('./research/ResearchApp'));

const root = ReactDOM.createRoot(document.getElementById('root'));

// Loading component for initial app load - matches the tenant loading style
// This is rendered inside ThemeProvider, so it can use MUI components
const InitialLoadingScreen = () => {
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      flexDirection: 'column',
      gap: 2,
      bgcolor: 'background.default',
      fontFamily: 'Roboto, sans-serif'
    }}>
      <CircularProgress size={40} />
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 500 }}>
          Loading Application...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we initialize
        </Typography>
      </Box>
    </Box>
  );
};

// Lazy load the tenant app using centralized loader
const TenantApp = lazy(loadTenantApp);

function getReviewRouteParams() {
  const pathname = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const adminView = params.get('adminView') === 'true';
  const initialSearch = params.get('q') ?? null;
  const appId = params.get('appId') ?? null;
  const sha256 = params.get('sha256') ?? null;

  if (pathname === '/review' || pathname === '/review/') {
    return { requestId: null, accessKeyFromUrl: null, adminView, initialSearch, appId, sha256 };
  }
  const match = pathname.match(/^\/review\/([^/]+)$/);
  if (!match) return null;
  const requestId = match[1];
  const accessKeyFromUrl = params.get('accessKey') ?? params.get('accesskey');
  return { requestId, accessKeyFromUrl, adminView, initialSearch, appId, sha256 };
}

function getResearchRouteParams() {
  const pathname = window.location.pathname;
  if (pathname === '/research' || pathname === '/research/') {
    return { runId: null };
  }
  const match = pathname.match(/^\/research\/([^/]+)$/);
  if (!match) return null;
  return { runId: match[1] };
}

// Main App component with lazy loading
const App = () => {
  const reviewParams = getReviewRouteParams();
  if (reviewParams) {
    return (
      <React.StrictMode>
        <ThemeProvider>
          <ReviewApp
            requestId={reviewParams.requestId}
            accessKeyFromUrl={reviewParams.accessKeyFromUrl}
            adminView={reviewParams.adminView}
            initialSearch={reviewParams.initialSearch}
            appId={reviewParams.appId}
            sha256={reviewParams.sha256}
          />
        </ThemeProvider>
      </React.StrictMode>
    );
  }

  const researchParams = getResearchRouteParams();
  if (researchParams) {
    return (
      <React.StrictMode>
        <ThemeProvider>
          <Suspense fallback={<InitialLoadingScreen />}>
            <ResearchApp {...researchParams} />
          </Suspense>
        </ThemeProvider>
      </React.StrictMode>
    );
  }

  return (
    <React.StrictMode>
      <ThemeProvider>
        <Suspense fallback={<InitialLoadingScreen />}>
          <TenantApp />
        </Suspense>
      </ThemeProvider>
    </React.StrictMode>
  );
};

// Render the app
root.render(
  <>
    {AesharedModeBanner ? <AesharedModeBanner /> : null}
    <App />
  </>,
);

